import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';
import InstallPrompt from './InstallPrompt';
import { LayoutDashboard, Users, BarChart3, Settings, LogOut, X, Wallet, Building2, Receipt, RefreshCw, TrendingDown, Repeat, FileSignature, KanbanSquare, Send, ListChecks, Mic } from 'lucide-react';
import BrandAvatar from './BrandAvatar';
import MarcaApp from './MarcaApp';
import { SECCIONES } from '../pages/admin/AdminSettings';
import { useLateral, ItemNav, BotonColapsar, BarraMobil, TapaCajon } from './lateral';

// el mismo degradé de marca que usan los portales
const DEGRADE = 'linear-gradient(160deg, #0B1C33 0%, #14487F 58%, #2E7DE2 100%)';
const FONDO_BASE = '#12365e';   // color sólido debajo, por si el degradé falla
const BORDE = '1px solid rgba(255,255,255,0.12)';

const navItems = [
  { grupo: 'Agencia' },
  { to: '/agencia', icon: Wallet, label: 'Panel', end: true },
  { to: '/agencia/tareas', icon: ListChecks, label: 'Tareas' },
  { to: '/agencia/clientes', icon: Building2, label: 'Clientes' },
  { to: '/agencia/propuestas', icon: FileSignature, label: 'Propuestas' },
  { to: '/agencia/proyectos', icon: KanbanSquare, label: 'Proyectos' },
  { to: '/agencia/cobros', icon: Receipt, label: 'Cobros' },
  { to: '/agencia/abonos', icon: Repeat, label: 'Abonos' },
  { to: '/agencia/renovaciones', icon: RefreshCw, label: 'Hosting' },
  { to: '/agencia/egresos', icon: TrendingDown, label: 'Egresos' },
  { to: '/agencia/reportes', icon: BarChart3, label: 'Reportes' },
  { to: '/agencia/reuniones', icon: Mic, label: 'Reuniones' },
  { to: '/agencia/envios', icon: Send, label: 'Envíos' },
  // Planificación: oculta por ahora, vuelve con otro formato. La ruta sigue existiendo.
  { grupo: 'CRM' },
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/clients', icon: Users, label: 'Clientes CRM' },
  { to: '/reports', icon: BarChart3, label: 'Reportes CRM' },
  { grupo: '' },
  { to: '/settings', icon: Settings, label: 'Configuración' },
];

export default function AdminLayout() {
  const { logout, user, theme, setTheme } = useAuth();
  const location = useLocation();
  const lateral = useLateral();
  const enConfig = location.pathname.startsWith('/settings');   // ahí desplegamos los subítems
  const marca = { backgroundColor: FONDO_BASE, backgroundImage: DEGRADE };
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>
      <BarraMobil fondo={marca} abierto={lateral.cajon} onAlternar={lateral.alternar_cajon}>
        <BrandAvatar src="/uploads/agencia/logo.png" name="Udream" size={34} />
        <div style={{ minWidth:0 }}>
          <div className="titulo">Udream</div>
          <div className="bajada"><MarcaApp /></div>
        </div>
      </BarraMobil>
      {/* El cajón vive acá adentro y no flotando sobre toda la pantalla: así no
          puede taparle nada al encabezado ni a la franja de superadmin. */}
      <div style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden', position:'relative' }}>
      <TapaCajon abierto={lateral.cajon} onClick={lateral.cerrar} />
      <div {...lateral.props}>
      <aside className={'sidebar' + (lateral.abierto ? ' abierto' : '')} style={{ width: lateral.ancho, height:'100%', ...marca, display:'flex', flexDirection:'column', position:'absolute',
        '--nav-color':'rgba(255,255,255,0.8)', '--nav-fuerte':'#fff', '--nav-hover':'rgba(255,255,255,0.12)',
        '--nav-activo-fondo':'#fff', '--nav-activo-texto':'#0B1C33' }}>
        <div hidden={lateral.celular} style={{ padding: lateral.abierto ? '24px 20px' : '24px 0', borderBottom:BORDE }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent: lateral.abierto ? 'flex-start' : 'center' }}>
            <BrandAvatar src="/uploads/agencia/logo.png" name="Udream" size={lateral.abierto ? 48 : 38} />
            {lateral.abierto && (
            <div className="sidebar-brand-text" style={{ minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:800, color:'#fff', lineHeight:1.2 }}>Udream</div>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4, fontSize:11, color:'rgba(255,255,255,0.6)', fontWeight:600 }}>
                <img src="/favicon-32x32.png?v=2" alt="" style={{ width:20, height:20, borderRadius:5, border:'1px solid rgba(255,255,255,0.7)' }} /> <MarcaApp />
              </div>
            </div>
            )}
          </div>
        </div>
        <div style={{ padding:'10px 10px 0' }}><NotificationBell abierto={lateral.abierto} /></div>
        <nav style={{ flex:1, padding:'8px 10px 12px', display:'flex', flexDirection:'column', gap:2, overflowY:'auto' }}>
          {navItems.map(item => item.grupo !== undefined ? (
            lateral.abierto
              ? <div key={'g' + item.grupo} style={{ fontSize:10, fontWeight:700, letterSpacing:1, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', padding:'12px 14px 4px' }}>{item.grupo}</div>
              : <div key={'g' + item.grupo} style={{ height:1, background:'rgba(255,255,255,0.14)', margin:'9px 14px' }} />
          ) : (
            <ItemNav key={item.to} to={item.to} end={item.end} icono={item.icon} label={item.label}
              abierto={lateral.abierto} onClick={lateral.cerrar} />
          ))}
          {enConfig && lateral.abierto && SECCIONES.map(s => (
            <NavLink key={s.key} to={'/settings#' + s.key} onClick={lateral.cerrar}
              style={() => {
                const activo = (location.hash || '#medios-pago').replace('#', '') === s.key;
                return { display:'block', padding:'7px 14px 7px 44px', borderRadius:8, fontSize:13, textDecoration:'none',
                  fontWeight: activo ? 700 : 500, color: activo ? '#fff' : 'rgba(255,255,255,0.65)',
                  background: activo ? 'rgba(255,255,255,0.14)' : 'transparent' };
              }}>{s.label}</NavLink>
          ))}
        </nav>
        <div style={{ padding: lateral.abierto ? '12px 14px 16px' : '12px 10px 16px', borderTop:BORDE }}>
          {!lateral.celular && <BotonColapsar colapsado={lateral.colapsado} onClick={lateral.alternar} abierto={lateral.abierto} />}
          {lateral.abierto && (
            <div style={{ display:'flex', alignItems:'center', gap:8, margin:'10px 0 8px' }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontSize:13, color:'#fff', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.display_name || user?.username}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.55)' }}><MarcaApp /></div>
              </div>
              <ThemeToggle theme={theme} onChange={setTheme} color="rgba(255,255,255,0.8)" />
            </div>
          )}
          <button onClick={logout} className={'nav-item accion' + (lateral.abierto ? '' : ' solo-icono')} title="Cerrar sesión">
            <LogOut size={19}/>{lateral.abierto && <span className="nav-texto">Cerrar sesión</span>}
          </button>
        </div>
      </aside>
      </div>
      <main className="contenido" style={{ flex:1, overflow:'auto', padding:28 }}><Outlet/></main>
      </div>

      <InstallPrompt />
    </div>
  );
}
